import { Vue, Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { BvMsgBoxOptions } from "bootstrap-vue";
import { RawLocation } from "vue-router";
import { BusyMixin } from "./BusyMixin";
import { TableMixin } from "./TableMixin";
import {
	auth,
	db,
	busy,
	toaster,
	VIPContact,
	IVIPContact,
	VIPContactRest,
	VIPContactDB,
	IVIPContactRestGetParams,
	VipContactCivility,
} from "@/loader";

@Component
export class VIPContactMixin extends Vue {
	public rest: typeof VIPContactRest = VIPContactRest;
	public db: VIPContactDB = db.contacts;
	public redirection: RawLocation = "";

	public get _item(): VIPContact | null {
		return null;
	}

	public get isNew(): boolean {
		return (this._item?.id || 0) < 0;
	}

	public get canCreate(): boolean {
		return auth.user?.capabilities.create_vip_contact || false;
	}

	public get canDelete(): boolean {
		return auth.user?.capabilities.delete_vip_contact || false;
	}

	public get canEdit(): boolean {
		return auth.user?.capabilities.edit_vip_contact || false;
	}

	public get canRead(): boolean {
		return auth.user?.capabilities.read_vip_contact || false;
	}

	public get shouldTrash(): boolean {
		return false;
	}
}

@Component
export class VIPContactEditMixin extends Mixins(VIPContactMixin, BusyMixin) {
	public changed: boolean = false;
	public focused: { [prop in keyof IVIPContact]?: boolean; } = {
		fk_company: false,
		phone: false,
		last_name: false,
		first_name: false,
		email: false,
		position: false,
		civility: false,
	 };
	public invalidFeedbacks: { [prop in keyof IVIPContact]?: string; } = {
		fk_company: window.wp.i18n._x("Company must be between 0 and 2E+64", "Contact", "vip-vue"),
		civility: window.wp.i18n._x("Civility must be Mr, Mrs or Ms", "Contact", "vip-vue"),
		phone: window.wp.i18n._x("Phone must not exceed 50 characters and must be a valid phone number", "Contact", "vip-vue"),
		last_name: window.wp.i18n._x("Last name can not be empty and must not exceed 255 characters", "Contact", "vip-vue"),
		first_name: window.wp.i18n._x("First name must not exceed 255 characters", "Contact", "vip-vue"),
		email: window.wp.i18n._x("Email must not exceed 255 characters and must be a valid email address", "Contact", "vip-vue"),
		position: window.wp.i18n._x("Position must not exceed 255 characters", "Contact", "vip-vue"),
	 };

	public get states(): { [prop in keyof IVIPContact]?: boolean | null; } {
		return {
			fk_company: this._item && this.focused.fk_company ? (this._item.fk_company >= 0 && this._item.fk_company <= 2E+64) : null,
			civility: this._item && this.focused.civility ? (Object.values(VipContactCivility).includes(this._item.civility)) : null,
			phone: this._item && this.focused.phone ? (this._item.phone.length <= 50 && /^\+?[0-9 \.\(\)-]+$/.test(this._item.phone)) : null,
			last_name: this._item && this.focused.last_name ? (!!this._item.last_name && this._item.last_name.length <= 255) : null,
			first_name: this._item && this.focused.first_name ? (this._item.first_name.length <= 255) : null,
			email: this._item && this.focused.email ? (this._item.email.length <= 255 && /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(this._item.email)) : null,
			position: this._item && this.focused.position ? (this._item.position.length <= 255) : null,
		 };
	}

	public get canCancel(): boolean {
		return this.canEdit && (this.isNew || this.changed);
	}

	public check(): boolean {
		return !Object.entries(this.states).some(([k, v]) => v === false);
	}

	public focusOn(prop: keyof IVIPContact): void {
		this.$set(this.focused, prop, true);
		this.changed = true;
	}

	public unfocusAll(): void {
		for (let p in this.focused) {
			// @ts-ignore
			this.focused[p] = false;
		}
	}

	public focusAll(): void {
		for (let p in this.focused) {
			// @ts-ignore
			this.focused[p] = true;
		}
	}

	public cancel(): void {
		if (this._item) {
			if (this.isNew) {
				this.db.remove(this._item, true);
				this.$emit("removed", this._item);
				this.$root.$emit("vip-contact-removed", this._item);
				if (this.redirection) {
					this.$router.push(this.redirection);
				}
			} else if (this.changed) {
				busy.start("Annulation des modifications ...");
				VIPContactRest.get(this._item).then(result => {
					if (result.success) {
						this.changed = false;
						this.unfocusAll();
						this.$emit("cancelled", this._item);
						this.$root.$emit("vip-contact-cancelled", this._item);
					}
				}).finally(() => {
					busy.stop();
				});
			} else {
				this.$emit("cancelled", this._item);
				this.$root.$emit("vip-contact-cancelled", this._item);
			}
		}
	}

	public removeMessageOption(shouldTrash: boolean = false): { message: string } & BvMsgBoxOptions {
		return {
			message: shouldTrash
				? "Êtes-vous sûr de vouloir mettre cet élément à la corbeille ?"
				: "Êtes-vous sûr de vouloir supprimer cet élément ?",
			title: shouldTrash ? "Mettre à la corbeille" : "Supprimer",
			cancelTitle: "Non",
			cancelVariant: "outline-secondary",
			okTitle: shouldTrash ? "Mettre à la corbeille" : "Supprimer",
			okVariant: "danger",
			centered: true,
		};
	}

	public remove() {
		if (!this._item) {
			return;
		} else if (this.isNew) {
			this.cancel();
		} else {
			if (!this.canDelete) {
				return;
			}
			let shouldTrash = this.shouldTrash;
			let config = this.removeMessageOption(shouldTrash);
			this.$bvModal.msgBoxConfirm(config.message, config).then((result: boolean | null) => {
				if (result && this._item) {
					busy.start(`${shouldTrash ? "Mise à la corbeille" : "Suppression"} en cours ...`);
					VIPContactRest.delete(this._item, { force: !shouldTrash }).then(result => {
						if (result.success) {
							toaster.toast({
								variant: "success",
								message: shouldTrash
									? "L'élément a été désactivée avec succès."
									: "L'élément a été supprimée avec succès.",
							});
							this.$emit(shouldTrash ? "trashed" : "removed", this._item);
							this.$root.$emit("vip-contact-" + (shouldTrash ? "trashed" : "removed"), this._item);
							if (!shouldTrash && this.redirection) {
								this.$router.push(this.redirection);
							}
						}
					}).finally(() => {
						busy.stop();
					});
				}
			});
		}
	}

	public save() {
		if (!this._item || !this.canEdit) {
			return;
		}
		if (!this.check()) {
			toaster.toast({ message: "Certaines informations ne sont pas valides.", variant: "warning", noAutoHide: false });
			return;
		}
		busy.start("Sauvegarde en cours ...");
		let isNew = this.isNew;
		let p = isNew ? VIPContactRest.create : VIPContactRest.update;
		p(this._item).then(result => {
			if (result.success) {
				this.changed = false;
				this.unfocusAll();
				if (isNew) {
					this.$emit("created", this._item);
					this.$root.$emit("vip-contact-created", this._item);
				}
				this.$emit("saved", this._item);
				this.$root.$emit("vip-contact-saved", this._item);
			}
		}).finally(() => {
			busy.stop();
		});
	}

	public unmount() {
		if (this.changed) {
			this.cancel();
		}
	}
}

interface VIPContactViewMixinOptions {
	redirection?: RawLocation;
	loadMessage?: string;
	getParams?: IVIPContactRestGetParams;
	loadDependencies?: () => void;
}

export function VIPContactViewMixin(options: VIPContactViewMixinOptions) {
	@Component
	class ViewMixin extends Mixins(VIPContactMixin, BusyMixin) {
		@Prop({ type: Number, required: true })
		public id!: number;

		public item: VIPContact | null = null;
		public redirection: RawLocation = options.redirection || "";
		public loadMessage: string = options.loadMessage || "";
		public getParams: IVIPContactRestGetParams = options.getParams || {};

		public get _item(): VIPContact | null {
			return this.item;
		}

		public loadDependencies() {}

		@Watch("id", { immediate: true })
		public idChanged(newValue?: number, oldValue?: number) {
			if (newValue && newValue !== oldValue && this.item?.id !== newValue) {
				this.item = this.db.get(newValue);
				if (!this.item) {
					if (newValue < 0) {
						this.$emit("cancelled", this.item);
						this.$root.$emit("vip-contact-cancelled", this._item);
						if (this.redirection) {
							this.$router.push(this.redirection);
						}
					} else {
						busy.start(this.loadMessage);
						VIPContactRest.get({ id: newValue }, this.getParams).then(result => {
							if (result.success && result.item) {
								this.item = result.item;
							}
						}).finally(() => {
							busy.stop();
						});
					}
				} else if (newValue > 0) {
					this.loadDependencies();
				}
			}
		}
	}

	return ViewMixin;
}

export function VIPContactEditViewMixin(options: VIPContactViewMixinOptions) {
	@Component
	class EditViewMixin extends Mixins(VIPContactEditMixin, BusyMixin, VIPContactViewMixin(options)) {
		public cancel(): void {
			if (this._item) {
				if (this.isNew) {
					this.db.remove(this._item, true);
					this.$emit("removed", this._item);
					this.$root.$emit("vip-contact-removed", this._item);
					if (this.redirection) {
						this.$router.push(this.redirection);
					}
				} else if (this.changed) {
					busy.start("Annulation des modifications ...");
					VIPContactRest.get(this._item, this.getParams).then(result => {
						if (result.success) {
							this.changed = false;
							this.unfocusAll();
							this.$emit("cancelled", this._item);
							this.$root.$emit("vip-contact-cancelled", this._item);
						}
					}).finally(() => {
						busy.stop();
					});
				} else {
					this.$emit("cancelled", this._item);
					this.$root.$emit("vip-contact-cancelled", this._item);
				}
			}
		}
	}

	return EditViewMixin;
}

@Component
export class VIPContactComponentMixin extends Mixins(VIPContactMixin) {
	@Prop({ type: VIPContact, required: true })
	public item!: VIPContact;

	public get _item(): VIPContact {
		return this.item;
	}
}

@Component
export class VIPContactEditComponentMixin extends Mixins(VIPContactEditMixin, VIPContactComponentMixin) {
}

@Component
export class VIPContactRefreshMixin extends Vue {
	public needRefresh: VIPContact | boolean = false;

	public setNeedRefresh(item: VIPContact) {
		this.needRefresh = item;
	}

	public setNeedRefreshTrue() {
		this.needRefresh = true;
	}

	public created() {
		this.$root.$on(["vip-contact-created"], this.setNeedRefreshTrue);
		this.$root.$on(["vip-contact-removed"], this.setNeedRefresh);
	}

	public beforeDestroy() {
		this.$root.$off(["vip-contact-created"], this.setNeedRefreshTrue);
		this.$root.$off(["vip-contact-removed"], this.setNeedRefresh);
	}

	public refresh() {}

	public maybeRefresh() {
		if (this.needRefresh) {
			this.needRefresh = false;
			this.refresh();
		}
	}

	public mounted() {
		this.maybeRefresh();
	}

	public activated() {
		this.maybeRefresh();
	}
}

@Component
export class VIPContactRefreshTableMixin extends Mixins(VIPContactRefreshMixin, TableMixin) {
	public maybeRefresh() {
		if (this.needRefresh) {
			if (this.needRefresh === true || this.table.localItems.includes(this.needRefresh)) {
				this.refresh();
			}
			this.needRefresh = false;
		}
	}
}
